import {
    type CSSProperties,
    type FC,
    type ReactNode,
    useContext,
    useMemo,
} from "react";
import { currency } from "#/Utilities/Generic/Numbers.ts";
import { isRecord } from "../../../../types/internal/utility";
import { type TableColumnMap, TableComponent } from "../../Forms/DragDrop/ComponentBases/TableComponent";
import { DragDropComponentErrorContext } from "../../Forms/DragDrop/DragDropComponent";
import { DragDropDataContext } from "../../Forms/DragDrop/DragDropRenderer";
import { type ComponentOptions } from "../../Forms/DragDrop/DragDropTypes";
import { type ColumnType, hasColumnsValue } from "../../Forms/DragDrop/ReorderableTableSettings";
import NoTranslate from "../../NoTranslate";
import { ReceiptTypeContext } from "../Receipt";

const A4_PADDING_SIZE = "0.5em";
const EMAIL_PADDING_SIZE = "2em";

export interface ReceiptProductTableComponent {
    showDiscountReason: boolean;
    showProductNote: boolean;
    columns: Array<ColumnType>;
}

export function isReceiptProductTableValue(value: unknown): value is ReceiptProductTableComponent {
    if(!hasColumnsValue(value)) {
        return false;
    }

    return "showDiscountReason" in value && typeof value.showDiscountReason === "boolean" &&
        "showProductNote" in value && typeof value.showProductNote === "boolean";
}

export const ReceiptProductTableComponent: FC<ComponentOptions> = ({ value, style }) => {
    const type = useContext(ReceiptTypeContext);

    const { indicators, products } = useContext(DragDropDataContext);

    const error = useContext(DragDropComponentErrorContext);

    let productIndex = -1;
    let productSearched = false;
    let priceIndex = -1;
    let priceSearched = false;

    const columnDefinitions = useMemo<TableColumnMap>((): TableColumnMap => {
        if(type === "TEXT_ONLY") {
            return {
                product: {
                    style: {
                        textAlign: "left",
                        width    : "100%",
                        wordBreak: "break-all",
                    },
                    format: (value, row) => {
                        let suffix = "";

                        if(isRecord(indicators)) {
                            if(typeof indicators[row.tax as string] === "string") {
                                suffix = indicators[row.tax as string] as string;
                            }
                        }

                        if(row.surcharge) {
                            suffix += `[${row.surcharge}]`;
                        }

                        return `${value}${suffix}`;
                    },
                },
                qty: {
                    style: {
                        textAlign: "center",
                    },
                },
                price: {},
            };
        }

        return {
            product: {
                style: (column, columns): CSSProperties => {
                    const results: CSSProperties = {
                        textAlign: "left",
                        width    : "100%",
                        wordBreak: "break-word",
                    };

                    if(type === "A4") {
                        results.paddingTop = A4_PADDING_SIZE;
                    }

                    if(type === "EMAIL" && column !== columns[0].column) {
                        results.paddingLeft = EMAIL_PADDING_SIZE;
                    }

                    return results;
                },
                format: (value, row) => {
                    let suffix = "";

                    if(isRecord(indicators)) {
                        if(typeof indicators[row.tax as string] === "string") {
                            suffix += indicators[row.tax as string] as string;
                        }
                    }

                    if(row.surcharge) {
                        suffix += `${row.surcharge}`;
                    }

                    return (
                        <>
                            {value}
                            {!!suffix && (
                                <sup
                                    style={{
                                        verticalAlign: "top",
                                    }}
                                >
                                    {suffix}
                                </sup>
                            )}
                        </>
                    );
                },
            },
            qty: {
                style: {
                    textAlign: "center",
                },
            },
            "case-qty": {
                style: {
                    textAlign: "center",
                },
            },
            "normal-price": {},
            "item-price"  : {},
            "case-price"  : {},
            "total-items" : {},
            discount      : {},
            price         : {},
            savings       : {},
        };
    }, [ type, indicators ]);

    if(!isReceiptProductTableValue(value)) {
        return error;
    }

    const { showDiscountReason, showProductNote, columns } = value;

    if(!Array.isArray(products)) {
        return null;
    }

    return (
        <TableComponent
            columns={columnDefinitions}
            data={products}
            value={columns}
            style={style}
            rowStyle={(row) => {
                if(type !== "A4") {
                    return {};
                }

                const style: CSSProperties = {
                    borderTop : "solid thin #BDBDBD",
                    paddingTop: A4_PADDING_SIZE,
                };

                if(Array.isArray(row.components)) {
                    if(
                        !(showProductNote && row.note) &&
                        !(showDiscountReason && row["discount-reason"]) &&
                        !row.components?.length
                    ) {
                        style.paddingBottom = A4_PADDING_SIZE;
                    }
                }

                return style;
            }}
            renderExtra={(row, columns, style) => {
                const columnCount = columns.length;

                if(!productSearched) {
                    productSearched = true;
                    productIndex = columns.findIndex(col => col.column === "product");
                }

                if(!priceSearched) {
                    priceSearched = true;
                    priceIndex = columns.findIndex(col => col.column === "price");
                }

                const components = Array.isArray(row.components) ? row.components : [];

                return (
                    <>
                        {showProductNote && !!row.note && (
                            <tr>
                                {productIndex > 0 && (
                                    <td style={style} colSpan={productIndex} />
                                )}
                                <td
                                    colSpan={columnCount - productIndex}
                                    style={{
                                        ...style,
                                        whiteSpace   : "pre-wrap",
                                        textAlign    : "left",
                                        paddingLeft  : "2em",
                                        paddingBottom: (
                                            type === "A4" &&
                                            !(showDiscountReason && row["discount-reason"]) &&
                                            !components.length
                                        ) ? A4_PADDING_SIZE : 0,
                                    }}
                                >
                                    - {row.note as string}
                                </td>
                            </tr>
                        )}
                        {showDiscountReason && !!row["discount-reason"] && (
                            <tr>
                                {productIndex > 0 && (
                                    <td style={style} colSpan={productIndex} />
                                )}
                                <td
                                    colSpan={columnCount - productIndex}
                                    style={{
                                        ...style,
                                        textAlign    : "left",
                                        paddingLeft  : "2em",
                                        paddingBottom: (
                                            type === "A4" &&
                                            !components.length
                                        ) ? A4_PADDING_SIZE : 0,
                                    }}
                                >
                                    - {row["discount-reason"] as string}
                                </td>
                            </tr>
                        )}
                        {
                            components.length > 0 && (productIndex >= 0 || priceIndex >= 0) && (
                                components.map((component, index) => {
                                    let icon = "-";

                                    if(component.type === "added") {
                                        icon = "+";
                                    }

                                    const componentStyle = { ...style };

                                    if(type === "A4" && index === components.length - 1) {
                                        componentStyle.paddingBottom = A4_PADDING_SIZE;
                                    }

                                    const columns: Array<ReactNode> = [];

                                    if(productIndex < priceIndex) {
                                        if(productIndex > 0) {
                                            columns.push(
                                                <td
                                                    key="first"
                                                    style={{
                                                        ...componentStyle,
                                                        paddingLeft: "0",
                                                    }}
                                                    colSpan={productIndex}
                                                />
                                            );
                                        }

                                        const productEnd = (priceIndex >= 0 ? priceIndex : columnCount);

                                        columns.push(
                                            <td
                                                key="component"
                                                colSpan={productEnd - productIndex}
                                                style={{
                                                    ...componentStyle,
                                                    textAlign  : "left",
                                                    paddingLeft: "2em",
                                                }}
                                            >
                                                {icon} {component.name}
                                            </td>
                                        );

                                        if(priceIndex >= 0) {
                                            const remaining = columnCount - priceIndex - 1;

                                            columns.push(
                                                <td
                                                    key="price"
                                                    style={{
                                                        ...componentStyle,
                                                        paddingRight: (remaining > 0 ? undefined : 0),
                                                        textAlign   : "right",
                                                    }}
                                                >
                                                    <NoTranslate>
                                                        {component.price !== 0 && currency(component.price)}
                                                    </NoTranslate>
                                                </td>
                                            );

                                            if(remaining > 0) {
                                                columns.push(
                                                    <td
                                                        key="last"
                                                        style={{
                                                            ...componentStyle,
                                                            paddingRight: 0,
                                                        }}
                                                        colSpan={remaining}
                                                    />
                                                );
                                            }
                                        }
                                    } else {
                                        if(priceIndex > 0) {
                                            columns.push(
                                                <td
                                                    key="first"
                                                    style={{
                                                        ...componentStyle,
                                                        paddingLeft: 0,
                                                    }}
                                                    colSpan={priceIndex}
                                                />
                                            );
                                        }

                                        columns.push(
                                            <td
                                                key="price"
                                                style={{
                                                    ...componentStyle,
                                                    paddingLeft: (priceIndex > 0 ? undefined : 0),
                                                    textAlign  : "right",
                                                }}
                                            >
                                                <NoTranslate>
                                                    {component.price !== 0 && currency(component.price)}
                                                </NoTranslate>
                                            </td>
                                        );

                                        const gap = (productIndex >= 0 ? productIndex : columnCount) - (priceIndex + 1);

                                        if(gap) {
                                            columns.push(
                                                <td
                                                    key="gap"
                                                    style={{
                                                        ...componentStyle,
                                                        padding: 0,
                                                    }}
                                                    colSpan={gap}
                                                />
                                            );
                                        }

                                        if(productIndex >= 0) {
                                            columns.push(
                                                <td
                                                    key="component"
                                                    colSpan={columnCount - productIndex}
                                                    style={{
                                                        ...componentStyle,
                                                        textAlign  : "left",
                                                        paddingLeft: "2em",
                                                    }}
                                                >
                                                    {icon} {component.name}
                                                </td>
                                            );
                                        }
                                    }

                                    return (
                                        <tr key={index}>
                                            {columns}
                                        </tr>
                                    );
                                })
                            )
                        }
                    </>
                );
            }}
        />
    );
};
